/**
 * 前端错误上报入口(可观测性契约 §4,2026-10-18;后端是自建 GlitchTip,走 Sentry 协议)。
 *
 * 上报范围(契约原话「只上报未捕获异常和 API 5xx」+ React 错误边界):
 *   1. 未捕获异常 / 未处理的 Promise 拒绝 —— Sentry GlobalHandlers;SDK 加载完成前发生的
 *      先在本文件排队(最多 {@link MAX_EARLY} 条),加载完补报;
 *   2. React 错误边界 —— PageErrorBoundary / H5ErrorBoundary 的 componentDidCatch 调
 *      {@link reportBoundaryError};
 *   3. API 5xx —— 请求封装(shared/api/httpClient、wecom-h5/wecomApi、语音陪练外链页)调
 *      {@link reportApiServerError}。4xx 是业务结果,不报。
 *
 * ★不进首屏:本文件不静态 import @sentry/*;window load 之后才取配置,DSN 有值才
 *   `import("./sentryClient")`(独立 vendor-sentry chunk)。DSN 为空 = 什么都不加载。
 * ★打点永不影响业务:所有入口 try/catch 吞掉。
 */
import { resolveErrorReportingConfig } from "./config";
import { normalizeApiPath } from "./path";
import type { CaptureContext, SentryHandle } from "./sentryClient";

type State = "idle" | "loading" | "ready" | "off";

const MAX_EARLY = 10;

let state: State = "idle";
let handle: SentryHandle | null = null;
let pending: Array<(h: SentryHandle) => void> = [];
let removeEarlyListeners: (() => void) | null = null;

function enqueue(fn: (h: SentryHandle) => void): void {
  try {
    if (state === "ready" && handle) {
      fn(handle);
    } else if (state === "idle" || state === "loading") {
      if (pending.length < MAX_EARLY) pending.push(fn);
    }
    // off:没配 DSN / 加载失败,直接丢
  } catch {
    /* 打点失败不影响业务 */
  }
}

function installEarlyListeners(): void {
  if (typeof window === "undefined") return;
  const onError = (ev: ErrorEvent) => {
    const err: unknown = ev.error ?? ev.message;
    enqueue((h) => h.captureException(err, { tags: { mechanism: "early_onerror" } }));
  };
  const onRejection = (ev: PromiseRejectionEvent) => {
    const reason: unknown = ev.reason;
    enqueue((h) => h.captureException(reason, { tags: { mechanism: "early_unhandledrejection" } }));
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  removeEarlyListeners = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    removeEarlyListeners = null;
  };
}

function finish(next: State, h: SentryHandle | null): void {
  removeEarlyListeners?.();
  state = next;
  handle = h;
  const queued = pending;
  pending = [];
  if (h) {
    for (const fn of queued) {
      try {
        fn(h);
      } catch {
        /* ignore */
      }
    }
  }
}

async function load(entry: string): Promise<void> {
  try {
    const config = await resolveErrorReportingConfig();
    if (!config) {
      finish("off", null);
      return;
    }
    const { startSentry } = await import("./sentryClient");
    finish("ready", startSentry(config, entry));
  } catch {
    finish("off", null);
  }
}

/**
 * 每个页面入口调一次(main / platform / tenant / wecom-h5 / advisor-h5 / voice-h5)。
 * 幂等。只挂两个轻量监听器,真正的配置读取和 SDK 加载推迟到 window load 之后。
 */
export function initErrorReporting(entry: string): void {
  try {
    if (state !== "idle" || typeof window === "undefined") return;
    state = "loading";
    installEarlyListeners();
    const start = () => void load(entry);
    if (document.readyState === "complete") setTimeout(start, 0);
    else window.addEventListener("load", start, { once: true });
  } catch {
    state = "off";
  }
}

export interface ApiServerErrorInfo {
  path: string;
  status: number;
  method?: string;
  errorCode?: string;
  traceId?: string;
  /** 哪个请求封装报的:http(PC 后台)/ wecom(企微 H5)/ voice(语音陪练外链)… */
  client?: string;
}

/** API 5xx(含网关 502/503/504)。<500 直接忽略。路径去 query、id 段归一后做分组指纹。 */
export function reportApiServerError(info: ApiServerErrorInfo): void {
  try {
    if (!(info.status >= 500)) return;
    const route = normalizeApiPath(info.path);
    const method = (info.method || "GET").toUpperCase();
    const errorCode = info.errorCode || "UNKNOWN";
    const ctx: CaptureContext = {
      level: "error",
      tags: {
        kind: "api_5xx",
        "api.route": route,
        "api.method": method,
        "api.status": String(info.status),
        "api.error_code": errorCode,
        "api.client": info.client || "http",
      },
      // X-Trace-ID 放 extra 不放 tag(高基数),用来和后端日志对齐
      extra: info.traceId ? { trace_id: info.traceId } : undefined,
      fingerprint: ["api_5xx", method, route, String(info.status)],
    };
    enqueue((h) => h.captureMessage(`API ${info.status} ${method} ${route}`, ctx));
  } catch {
    /* ignore */
  }
}

/** React 错误边界兜到的渲染异常。 */
export function reportBoundaryError(
  error: unknown,
  componentStack: string | undefined,
  boundary: string
): void {
  try {
    enqueue((h) =>
      h.captureException(error, {
        tags: { mechanism: "react_error_boundary", boundary },
        contexts: componentStack ? { react: { componentStack } } : undefined,
      })
    );
  } catch {
    /* ignore */
  }
}

/** 仅测试用:重置模块状态。 */
export function __resetErrorReportingForTest(): void {
  removeEarlyListeners?.();
  state = "idle";
  handle = null;
  pending = [];
}
